import { Radio } from '@base-ui/react/radio';
import { usePostHog } from '@posthog/react';
import { CheckIcon, ThumbsDownIcon, ThumbsUpIcon } from 'components/icons';
import { Button } from 'components/ui/button';
import { MessageFooter } from 'components/ui/message';
import { RadioGroup } from 'components/ui/radio-group';
import { Textarea } from 'components/ui/textarea';
import { Tooltip } from 'components/ui/tooltip';
import { type ComponentProps, type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useAddUserFeedbackMutation } from 'store/api/feedbackApi';
import { FeedbackCategory, type FeedbackCreateDto, type FeedbackSentiment } from 'types/feedback';
import { collectBrowserMetadata } from 'utils/browserMetadata';
import { cn } from 'utils/cn';
import { useAiChatOptionPick } from '../aiChatOptionPickContext';
import { BOTTOM_GAP } from '../floating/panelGeometry';

const COMMENT_MAX_LENGTH = 5000;

// What went wrong, in the panel's order. A display problem is the stored UI bug.
const CATEGORIES = [
    { value: FeedbackCategory.NOT_FACTUALLY_CORRECT, labelId: 'feedback.form.category.not-factually-correct' },
    { value: FeedbackCategory.INCOMPLETE_INFORMATION, labelId: 'feedback.form.category.incomplete-information' },
    { value: FeedbackCategory.DID_NOT_FOLLOW_REQUEST, labelId: 'feedback.form.category.did-not-follow-request' },
    { value: FeedbackCategory.UI_BUG, labelId: 'ai-chat.feedback.display-problem' },
    { value: FeedbackCategory.OTHER, labelId: 'feedback.form.category.other' },
];

// The like burst's eight dots, 20px out at 45° apart.
const PARTICLES = [
    '[--px:0px] [--py:-20px]',
    '[--px:14px] [--py:-14px]',
    '[--px:20px] [--py:0px]',
    '[--px:14px] [--py:14px]',
    '[--px:0px] [--py:20px]',
    '[--px:-14px] [--py:14px]',
    '[--px:-20px] [--py:0px]',
    '[--px:-14px] [--py:-14px]',
];

// The dots' 600ms and their stagger; the burst class comes off after it so the next like plays it again.
const BURST_MS = 700;

type AnswerActionProps = ComponentProps<typeof Button> & {
    label: string;
    /** Keeps the tooltip up through a click, for a press answered in place, such as Copy's check. */
    closeOnClick?: boolean;
};

// The 28px actions' areas, 32px and 44px on touch, meet across the gap.
const ACTIONS_ROW = '-ms-1.5 flex-wrap gap-1 px-0 pointer-coarse:gap-4';

/** One of an answer's actions: a ghost icon button, named by its tooltip. */
export function AnswerAction({ label, closeOnClick, className, ...props }: AnswerActionProps) {
    return (
        <Tooltip content={label} closeOnClick={closeOnClick}>
            <Button
                variant="ghost"
                size="icon-sm"
                aria-label={label}
                className={cn(
                    'text-muted-foreground hover:bg-muted hover:text-foreground new:[&_svg[stroke]]:stroke-[1.5px] size-7 rounded-md',
                    className
                )}
                {...props}
            />
        </Tooltip>
    );
}

/**
 * "Feedback sent", rising into its place (text states swap). Hidden, it holds its place unseen and unread, for a
 * row that must not move as it comes.
 */
export function SentNote({ className, hidden = false }: { className?: string; hidden?: boolean }) {
    const intl = useIntl();
    // Mounted at the recipe's start, then let go, so it has a state to rise from.
    const [entering, setEntering] = useState(true);
    useEffect(() => {
        const frame = requestAnimationFrame(() => setEntering(false));
        return () => cancelAnimationFrame(frame);
    }, []);
    return (
        <span aria-hidden={hidden || undefined} className={cn('text-caption', className, hidden && 'invisible')}>
            <span className={cn('t-text-swap', entering && 'is-enter-start')}>
                {intl.formatMessage({ id: 'ai-chat.feedback.sent' })}
            </span>
        </span>
    );
}

/** Posts a rating with the browser's metadata, then tells PostHog what was rated. */
export function usePostFeedback(): (feedback: FeedbackCreateDto) => Promise<void> {
    const posthog = usePostHog();
    const [addFeedback] = useAddUserFeedbackMutation();
    // A failed post is told by the API's own error toast.
    return (feedback) =>
        addFeedback({ ...feedback, ...collectBrowserMetadata() })
            .unwrap()
            .then(() => {
                posthog.capture('ai_feedback_given', {
                    sentiment: feedback.sentiment,
                    category: feedback.category,
                    source: feedback.source === 'IMPROVE' ? 'improve' : 'ai_chat',
                });
            });
}

/** Scrolls the nearest scroll container above the form by as far as the form's bottom passes its own. */
function revealForm(form: HTMLElement) {
    let box = form.parentElement;
    while (box && !(/(auto|scroll)/.test(getComputedStyle(box).overflowY) && box.scrollHeight > box.clientHeight)) {
        box = box.parentElement;
    }
    if (!box) {
        return;
    }
    const view = window.visualViewport;
    const viewBottom = view ? view.offsetTop + view.height : window.innerHeight;
    // The row of floating buttons along a page's bottom, or 16px above an on-screen keyboard, which covers that row.
    const margin = viewBottom < window.innerHeight ? 16 : BOTTOM_GAP;
    const overshoot =
        form.getBoundingClientRect().bottom - (Math.min(box.getBoundingClientRect().bottom, viewBottom) - margin);
    if (overshoot > 0) {
        // Instant: the panel's growth is the motion, and a smooth scroll would lag behind it.
        box.scrollBy({ top: overshoot, behavior: 'instant' });
    }
}

export type PostRating = (sentiment: FeedbackSentiment, category: FeedbackCategory, comment?: string) => Promise<void>;

type AnswerRatingProps = {
    /** Posts one rating of the answer. */
    post: PostRating;
    /** Says "Feedback sent" through the host's status region. */
    announce: (text: string) => void;
    /** Lays out the row from the two thumbs and whether "Feedback sent" shows. */
    children: (thumbs: ReactNode, sent: boolean) => ReactNode;
    /**
     * Keeps the open form in view in its nearest scroll container, above an on-screen keyboard too, as it opens,
     * grows or takes focus.
     */
    reveal?: boolean;
};

/**
 * An AI answer's thumbs and its "What went wrong?" form, for any host: Good answer posts at once; Bad answer opens
 * the form under the row, and Send feedback posts it, says "Feedback sent" and gives focus back to Bad answer. Both
 * post the sentiment, a category (`NONE` for a good answer or none picked) and the comment. The form keeps its keys:
 * Esc closes it alone, and no Enter leaves it, where a form around it would save on one.
 */
export function AnswerRating({ post, announce, children, reveal = false }: AnswerRatingProps) {
    const intl = useIntl();
    const [vote, setVote] = useState<FeedbackSentiment | null>(null);
    const [bursting, setBursting] = useState(false);
    const [open, setOpen] = useState(false);
    const [sent, setSent] = useState(false);
    const [sending, setSending] = useState(false);
    const [category, setCategory] = useState<FeedbackCategory | null>(null);
    const [comment, setComment] = useState('');
    const badRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const titleId = useId();

    const message = (id: string) => intl.formatMessage({ id });
    const goodLabel = message('ai-chat.feedback.good');
    const badLabel = message('ai-chat.feedback.bad');

    useEffect(() => {
        if (!bursting) {
            return undefined;
        }
        const timer = setTimeout(() => setBursting(false), BURST_MS);
        return () => clearTimeout(timer);
    }, [bursting]);

    // Focus moves into the panel as it opens.
    useEffect(() => {
        if (open) {
            panelRef.current?.querySelector<HTMLElement>('[role="radio"]')?.focus();
        }
    }, [open]);

    // Run from the panel's own growth: an effect as it opens comes while the panel still clips the form.
    useEffect(() => {
        const form = panelRef.current;
        if (!reveal || !open || !form) {
            return undefined;
        }
        const run = () => revealForm(form);
        const resized = new ResizeObserver(run);
        resized.observe(form.closest('.t-acc-panel') ?? form);
        form.addEventListener('focusin', run);
        window.visualViewport?.addEventListener('resize', run);
        return () => {
            resized.disconnect();
            form.removeEventListener('focusin', run);
            window.visualViewport?.removeEventListener('resize', run);
        };
    }, [reveal, open]);

    const like = () => {
        if (vote === 'positive') {
            return;
        }
        const before = vote;
        setVote('positive');
        setBursting(true);
        setOpen(false);
        setSent(false);
        post('positive', FeedbackCategory.NONE).catch(() => setVote(before));
    };

    const closePanel = () => {
        setOpen(false);
        badRef.current?.focus();
    };

    const send = () => {
        if (sending) {
            return;
        }
        setSending(true);
        post('negative', category ?? FeedbackCategory.NONE, comment || undefined)
            .then(() => {
                setVote('negative');
                setCategory(null);
                setComment('');
                setSent(true);
                announce(message('ai-chat.feedback.sent'));
                closePanel();
            })
            .catch(() => {})
            .finally(() => setSending(false));
    };

    const downed = open || vote === 'negative';

    const thumbs = (
        <>
            <AnswerAction
                label={goodLabel}
                aria-pressed={vote === 'positive'}
                data-liked={vote === 'positive' ? 'true' : 'false'}
                onClick={like}
                className={cn('t-like relative', bursting && 'is-bursting')}
            >
                <span className="t-like-icon inline-flex">
                    <ThumbsUpIcon className="t-like-heart size-4" />
                </span>
                <span className="t-like-particles" aria-hidden="true">
                    {PARTICLES.map((particle) => (
                        <i key={particle} className={particle} />
                    ))}
                </span>
            </AnswerAction>
            <AnswerAction
                ref={badRef}
                label={badLabel}
                aria-pressed={vote === 'negative'}
                aria-expanded={open}
                onClick={() => {
                    setOpen(!open);
                    setSent(false);
                }}
                // Pressed, it is its filled glyph alone: no tile while its form is open.
                className={cn(
                    'aria-expanded:text-foreground-secondary aria-expanded:bg-transparent',
                    downed && 'text-foreground-secondary'
                )}
            >
                <ThumbsDownIcon className={cn('size-4', downed && 'fill-current')} />
            </AnswerAction>
        </>
    );

    return (
        <div className="flex flex-col">
            {children(thumbs, sent)}
            <div className="t-acc" data-open={open ? 'true' : 'false'}>
                <div className="t-acc-panel">
                    <div className="t-acc-panel-inner">
                        <div
                            ref={panelRef}
                            role="group"
                            aria-labelledby={titleId}
                            // Esc closes the form alone: claimed and stopped here, it reaches neither the chat
                            // panel nor the answer box around it, which would close. An Enter adds a line in the
                            // comment and goes no further: a record's section saves on Ctrl/Cmd+Enter.
                            onKeyDown={(event) => {
                                if (event.key === 'Escape') {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    closePanel();
                                } else if (event.key === 'Enter') {
                                    event.stopPropagation();
                                }
                            }}
                            className="border-border bg-card new:border-0 new:shadow-card new:outline-hidden mt-2 flex flex-col gap-3 rounded-xl border p-4"
                        >
                            <p id={titleId} className="text-section text-foreground m-0 text-balance">
                                {message('ai-chat.feedback.what-went-wrong')}
                            </p>
                            <RadioGroup
                                aria-label={message('feedback.form.category.label')}
                                value={category}
                                onValueChange={(value) => setCategory(value as FeedbackCategory)}
                                className="flex flex-wrap gap-1.5"
                            >
                                {CATEGORIES.map(({ value, labelId }) => (
                                    <Radio.Root
                                        key={value}
                                        value={value}
                                        className="group/reason text-meta border-border-control bg-card text-foreground-secondary hover:bg-muted hover:text-foreground focus-visible:ring-ring/50 data-checked:border-primary data-checked:bg-primary-wash data-checked:text-primary-ink data-checked:hover:bg-primary-wash inline-flex min-h-8 cursor-pointer items-center gap-1 rounded-full border px-3 py-1 text-start transition-[color,background-color,border-color,scale] [transition-duration:var(--duration-quick),var(--duration-quick),var(--duration-quick),var(--press-dur)] ease-out outline-none focus-visible:ring-3 data-checked:font-medium motion-safe:will-change-transform motion-safe:active:scale-(--press-scale)"
                                    >
                                        <CheckIcon className="-ms-0.5 hidden size-3.5 group-data-checked/reason:block" />
                                        {message(labelId)}
                                    </Radio.Root>
                                ))}
                            </RadioGroup>
                            <Textarea
                                aria-label={message('feedback.form.comment.label')}
                                placeholder={message('feedback.form.comment.placeholder')}
                                rows={2}
                                maxLength={COMMENT_MAX_LENGTH}
                                value={comment}
                                onChange={(event) => setComment(event.target.value)}
                                className="bg-card md:text-body1 min-h-11 resize-none px-3 shadow-none"
                            />
                            <div className="flex flex-wrap items-center justify-end gap-2">
                                <Button
                                    variant="ghost"
                                    onClick={closePanel}
                                    className="text-meta text-foreground-secondary h-8 rounded-lg px-3"
                                >
                                    {message('cancel')}
                                </Button>
                                <Button
                                    variant="secondary"
                                    aria-disabled={sending || undefined}
                                    onClick={send}
                                    className="text-meta h-8 rounded-lg px-3 font-semibold aria-disabled:cursor-default"
                                >
                                    {message('ai-chat.feedback.send')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

interface Props {
    /** The stored answer the thumbs are about. Without it, as before the answer is stored, the row has none. */
    aiChatMessageId?: number;
    /** The row's other actions, before the thumbs. */
    children?: ReactNode;
}

/**
 * An answer's action row and its feedback: the thumbs after the row's other actions, "Feedback sent" after them,
 * said through the conversation's status region.
 */
export default function AiChatFeedback({ aiChatMessageId, children }: Props) {
    const { announce } = useAiChatOptionPick();
    const postFeedback = usePostFeedback();

    if (typeof aiChatMessageId !== 'number') {
        return (
            <MessageFooter role="group" className={ACTIONS_ROW}>
                {children}
            </MessageFooter>
        );
    }

    return (
        <AnswerRating
            post={(sentiment, category, comment) =>
                postFeedback({ source: 'AI_CHAT', aiChatMessageId, sentiment, category, comment })
            }
            announce={announce}
        >
            {(thumbs, sent) => (
                <MessageFooter role="group" className={ACTIONS_ROW}>
                    {children}
                    {thumbs}
                    {sent ? <SentNote className="text-muted-foreground ms-1.5" /> : null}
                </MessageFooter>
            )}
        </AnswerRating>
    );
}
